<template>
  <div>
    <vue-title :title="$t('identityProvider:fraudControl:challenge:title')"></vue-title>
    <h1 class="section-title">{{ $t('identityProvider:fraudControl:challenge:title') }}</h1>
    <Form v-slot="{ validate, setErrors, setFieldError }">
      <form ref="form"
            action="authentication/challenge"
            @submit.prevent="validateBeforeSubmit(validate, setErrors, setFieldError)">

        <div class="validation-summary-errors alert alert-danger fade show"
             role="alert"
             v-show="!model.showError && model.errorMessage"
             data-valmsg-summary="true" data-test-element="idp-alert-danger-secureNow-challenge">
          <i class="material-icons">warning</i>
          <span v-if="model.errorMessage">{{ model.errorMessage }}</span>
          <span v-else class="alert-message" data-test-element="alert-message">{{ $t('validation:summary') }}</span>
        </div>

        <div class="validation-summary-errors alert alert-danger fade show"
             role="alert"
             v-if="hasAnyFieldErrors">
          <i class="material-icons">error</i>
          <span class="alert-message" data-test-element="alert-message">{{ $t('validation:summary') }}</span>
        </div>

        <fieldset>
          <div class="panel-message">
            <span v-if="model.isSecurityQuestionsRequired">{{ $t('identityProvider:fraudControl:challenge:resetSecurityQuestionsInstructions') }}</span>
            <span v-else>{{ $t('identityProvider:fraudControl:challenge:instructions') }}</span>
          </div>
          <div class="form-container">
            <div class="form-group">
              <label data-test-element="challenge-question">
                <span class="item label">{{ $t('identityProvider:fraudControl:challenge:prompt') }}</span>
                {{ model.prompt }}
              </label>
            </div>
            <div class="form-group">
              <label class="security-answer">
                <span class="item label">{{ $t('identityProvider:fraudControl:challenge:answer') }}</span>
                <Field name="Answer"
                       :rules="answerRules"
                       v-model="model.answer"
                       v-slot="{ field, meta }">
                  <input v-bind="field"
                         :type="showAnswer ? 'text' : 'password'"
                         id="txtAnswer"
                         name="Answer"
                         v-focus
                         class="w-100 mb-2 input-text control"
                         :class="{ 'input-validation-error': submitted && !meta.valid }"
                         autocomplete="off"
                         :aria-invalid="(submitted && !meta.valid) ? 'true' : 'false'"
                         aria-describedby="answer-error"
                         data-test-element="Answer" />
                </Field>

                <ErrorMessage v-if="submitted" name="Answer" v-slot="{ message }">
                  <span class="field-validation-error" id="answer-error" role="alert">{{ message }}</span>
                </ErrorMessage>
              </label>

              <label class="show-answer checkbox">
                <input type="checkbox"
                       data-test-element="checkbox"
                       :checked="showAnswer"
                       @change="changeVisibility" />
                <i class="material-icons checkbox-icon"></i>
                {{ $t('shared:action:showAnswer') }}
              </label>
            </div>

            <div class="form-actions mb-3">
              <button type="submit" class="button" name="Action:SecurityQuestion" data-test-element="submitBtn" :disabled="disabled">{{ $t('shared:action:continue') }}</button>
              <a @click="cancel(cancelLink)" class="link link-text-transform button-secondary">
                {{ $t('shared:action:cancel') }}
              </a>
            </div>
          </div>
        </fieldset>
      </form>
    </Form>
  </div>
</template>

<script>
  import redirectHandler from 'utils/redirect';
  import { Form, Field, ErrorMessage } from 'vee-validate';
  import validation from 'utils/validation-mixin';
  import { applyServerErrorsFromAxios, focusFirstInvalid } from 'utils/validation-mixin';

  export default {
    name: 'MfaChallenge',
    components: { Form, Field, ErrorMessage },
    mixins: [validation],
    props: {
      mfaChallenge: {
        type: Object,
        required: true
      },
      cancelLink: { type: String, default: 'authentication/logincancel' }
    },

    data: () => ({
      model: {
        prompt: null,
        answer: null,
        isMFAChallenge: true,
        showError: false,
        errorMessage: null
      },
      disabled: false,
      submitted: false,
      hasAnyFieldErrors: false,
      messageIcon: null,
      alertContent: '',
      showAnswer: false,
    }),

    computed: {
      instructionsText() {
        return this.$t('identityProvider:fraudControl:challenge:instructions');
      },
      answerRules() {
        const rules = { required: true };
        return rules;
      }
    },

    methods: {
      changeVisibility() {
        this.showAnswer = !this.showAnswer;
      },
      async validateBeforeSubmit(validate, setErrors, setFieldError) {
        this.model.errorMessage = null;
        this.alertContent = '';
        this.messageIcon = null;
        this.hasAnyFieldErrors = false;
        this.submitted = true;

        const result = await validate();
        const isValid = result === true || result?.valid === true;
        if (isValid) {
          await this.post(setErrors, setFieldError);
        } else {
          this.hasAnyFieldErrors = true;
          focusFirstInvalid(this.$refs.form);
        }
      },

      async post(setErrors, setFieldError) {
        const form = this.$refs.form;
        if (!form) return;
        this.disabled = true;
        try {
          const response = await this.$http.post(form.action, this.model);
          redirectHandler.checkAndRedirect(response, this.$router);
        } catch (error) {
          const resp = error?.response;

          // If there's a new prompt, the user has a new question to answer.
          if (resp?.data && resp.data.prompt) {
            // keep original property names and replace model with server payload
            this.model = Object.assign({}, this.model, resp.data);
            return;
          }

          // vee-validate v4 mapping (ASP.NET Core 400 ModelState)
          if (applyServerErrorsFromAxios(error, { setErrors, setFieldError })) {
            focusFirstInvalid(form);
            return;
          }

          // Legacy: delegate to validateResponse (summary/field errors from server)
          if (this.validateResponse?.(form, resp)) {
            return;
          }         

          // Unexpected error: surface message in banner and rethrow for global handler
          const msg = resp?.data?.message || error?.message;
          if (msg) {
            this.model.errorMessage = msg;
            this.messageIcon = 'warning';
            this.alertContent = 'validation-summary-errors alert alert-danger fade show';
          }
          throw error;
        } finally {
          this.disabled = false;
        }
      },

      async cancel(path) {
        const response = await this.$http.post(path);
        redirectHandler.checkAndRedirect(response, this.$router);
      },

      closeBanner() {
        this.model && (this.model.errorMessage = null);
      }
    },

    created() {
      try {
        const data = this.mfaChallenge?.data || {};
        this.model.prompt = data.prompt ?? null;
        this.model.isMFAChallenge = data.isMFAChallenge ?? this.model.isMFAChallenge;
        setTimeout(() => {
          window.scrollTo(0, 0);
        })
      } catch (error) {
        let form = this.$refs.form;
        if (this.validateResponse(form, error.response)) {
          return;
        }
        throw error;
      }
    }
  };
</script>